import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Button,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  Home,
  People,
  Favorite,
  Notifications,
  Add,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';

const AgentDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { properties, clients, matches } = useAppStore();
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return <LinearProgress />;

  const activeProperties = properties.filter(p => p.isActive).length;
  const activeClients = clients.filter(c => c.status !== 'inactive').length;
  const myRecentProperties = properties.filter(p => p.agentId === user?.id).slice(0, 3);

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif', color: 'primary.main' }}>
            Marhaba, {user?.profile.firstName}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your listings and client pipeline efficiently.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
           <Button variant="outlined" startIcon={<Notifications />}>Feed</Button>
           <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/properties/create')}>New Listing</Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="textSecondary" variant="subtitle2" sx={{ fontWeight: 600 }}>Active Listings</Typography>
                <Home color="primary" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{activeProperties}</Typography>
              <Typography variant="caption" color="text.secondary">Total units under management</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="textSecondary" variant="subtitle2" sx={{ fontWeight: 600 }}>Active Leads</Typography>
                <People color="primary" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{activeClients}</Typography>
              <Typography variant="caption" color="text.secondary">In your Moroccan Legal Pipeline</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="textSecondary" variant="subtitle2" sx={{ fontWeight: 600 }}>Matches Found</Typography>
                <Favorite color="primary" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{matches.length}</Typography>
              <Typography variant="caption" color="text.secondary">AI-generated recommendations</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="textSecondary" variant="subtitle2" sx={{ fontWeight: 600 }}>Market Growth</Typography>
                <TrendingUp color="success" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>+12.4%</Typography>
              <Typography variant="caption" color="text.secondary">Average yield in your focus area</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
            Client Pipeline
          </Typography>
          <Card sx={{ borderRadius: 2, mb: 4 }}>
            <CardContent sx={{ p: 0 }}>
              {clients.length > 0 ? (
                <List>
                  {clients.slice(0, 5).map((client, index) => (
                    <ListItem key={client.id} divider={index < 4}>
                      <ListItemAvatar>
                        <Avatar src={client.profile.avatar} sx={{ bgcolor: 'rgba(197, 160, 89, 0.1)', color: 'primary.main' }}>
                          {client.profile.firstName[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={`${client.profile.firstName} ${client.profile.lastName}`}
                        secondary={`Stage: ${client.status.toUpperCase()}`}
                        primaryTypographyProps={{ fontWeight: 700 }}
                      />
                      <Button variant="outlined" size="small" onClick={() => navigate(`/clients/${client.id}`)}>View File</Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">No active clients found.</Typography>
                  <Button sx={{ mt: 2 }} variant="outlined" startIcon={<Add />} onClick={() => navigate('/clients')}>Register Lead</Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
            My Active Listings
          </Typography>
          <Grid container spacing={2}>
            {myRecentProperties.length > 0 ? myRecentProperties.map(p => (
              <Grid item xs={12} sm={6} key={p.id}>
                <Card sx={{ display: 'flex', height: 120 }}>
                  <Box 
                    sx={{ 
                      width: 120, 
                      bgcolor: 'grey.100',
                      backgroundImage: `url(${p.metadata.images?.[0]})`,
                      backgroundSize: 'cover'
                    }} 
                  />
                  <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>{p.metadata.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.metadata.price} {p.metadata.currency}</Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                       <Chip label={`${p.analytics?.views || 0} Views`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                       <Button size="small" sx={{ ml: 'auto', p: 0, minWidth: 0 }} onClick={() => navigate(`/properties/${p.id}`)}>Edit</Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                  <Typography variant="body2" color="text.secondary">You haven't added any listings yet.</Typography>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 4, bgcolor: '#3C3E3F', color: 'white' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Quick Actions</Typography>
              <Stack spacing={2}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  startIcon={<Add />} 
                  sx={{ bgcolor: 'primary.main', color: 'white' }}
                  onClick={() => navigate('/properties/create')}
                >
                  Create Listing
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  startIcon={<People />} 
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                  onClick={() => navigate('/clients')}
                >
                  New Client
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ border: '1px solid rgba(197, 160, 89, 0.2)', bgcolor: 'rgba(197, 160, 89, 0.02)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TrendingUp sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Moroccan Market Yields</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2">Casablanca Finance City</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>+6.2%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={62} sx={{ height: 4, borderRadius: 2 }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2">Rabat Souissi</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>+4.8%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={48} sx={{ height: 4, borderRadius: 2 }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2">Tanger Waterfront</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>+7.5%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={75} sx={{ height: 4, borderRadius: 2 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AgentDashboardPage;
